import { useEffect } from 'react';

import { backendApis } from '@/backendApis';
import { electronApis } from '@/features/electron-api';
import { isSsoRequiredError } from '@/features/login/sso';
import { logger } from '@/lib/logger';
import { logoutUser } from '@/utils/auth';

/**
 * Register this device with the backend once per app launch.
 *
 * A device only gets its addressable `device_slug` (used as `device:{toolkit}@{slug}`
 * for on-device tools) from `/sync/user` or `push/register`. Both can be skipped for a
 * whole launch: `/sync/user` runs only from the login screen, so a session restored from
 * the keychain never calls it, and `push/register` needs an APNs token that dev builds
 * never get. A device in that state has tools advertised via the manifest but no slug,
 * so the agent can't address it. Calling `/sync/user` here closes the gap; the backend
 * treats it as an idempotent upsert for an existing user.
 */
export function useRegisterDevice(): void {
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const accessToken = await electronApis.auth.getAccessToken();
        if (!accessToken || cancelled) return;
        await backendApis.user.syncUser(accessToken);
      } catch (error) {
        if (isSsoRequiredError(error)) {
          await logoutUser('token_revoked');
          return;
        }
        // Non-fatal: the next launch (or a login) retries. Log so a missing
        // slug is diagnosable from the renderer log.
        logger.warn('Device registration on launch failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
